import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { JsonParam, StringParam, useQueryParam } from "use-query-params"
import { useIntl } from "react-intl"
import { WingsCareerCard } from "@wingspans-ui"
import { RadioButton } from "primereact/radiobutton"
import { useAllCareers } from "../../data/graphql"
import { FirebaseContext } from "../Firebase"
import CardCareers from "../cards/cards-careers"
import { calcMatchStrength, removeMissingCareers } from "../assessment/utils/utils"
import ExploreUnifiedFiltersDropdown from "./ExploreUnifiedFiltersDropdown"
import LocalExplore from "./LocalExplore"

const CAREERS_PER_PAGE = 9

const careersSearchKeys = [
  { name: "title", weight: 0.5 },
  { name: "career_related_careers_titles_text", weight: 0.2 },
  { name: "career_keywords_text", weight: 0.3 },
  { name: "career_job_categories_text", weight: 0.1 },
  { name: "career_pathways_text", weight: 0.075 },
  { name: "career_education_level", weight: 0.025 },
]

const MATCH_TYPE_COMBINED = "combined"
const MATCH_TYPE_PERSONALITY = "personality"
const MATCH_TYPE_STAY_STEADY = "stay steady"
const MATCH_TYPE_CHANGE_COURSE = "change course"

const CAREERS_FILTER_KEYS = {
  activity: "activity",
  dress: "dress",
  education: "education",
  salary: "salary",
  personality: "personality",
  pathways: "pathways",
}
const FILTER_ID_SEPARATOR = "::"
const ENABLE_EXCLUSIVE_OPTION_HIDING = false

const SALARY_RANGE_BY_IMPORTANCE = {
  "Not that Important": [0, 51000],
  Important: [51000, 150000],
  "Very Important": [150000, Infinity],
}

const normalizeCareerMatches = (matches) => {
  if (!Array.isArray(matches)) {
    return []
  }

  return matches
    .map((match) => {
      if (typeof match === "string") {
        return { slug: match }
      }

      return match
    })
    .filter((match) => Boolean(match?.slug))
}

const normalizeRefinementList = (refinementList) => {
  const normalized = {
    [CAREERS_FILTER_KEYS.activity]: [],
    [CAREERS_FILTER_KEYS.dress]: [],
    [CAREERS_FILTER_KEYS.education]: [],
    [CAREERS_FILTER_KEYS.salary]: [],
    [CAREERS_FILTER_KEYS.personality]: [],
    [CAREERS_FILTER_KEYS.pathways]: [],
  }

  if (!refinementList || typeof refinementList !== "object") {
    return normalized
  }

  Object.keys(normalized).forEach((key) => {
    const nextValues = refinementList[key]
    normalized[key] = Array.isArray(nextValues) ? nextValues : []
  })

  return normalized
}

const includesAnyValue = (source, selectedValues) => {
  if (!selectedValues.length) {
    return true
  }

  const normalizeSelection = (value) => String(value || "").toLowerCase().trim()

  const flattenToText = (value) => {
    if (Array.isArray(value)) {
      return value.map((item) => flattenToText(item)).join(" ")
    }

    if (value && typeof value === "object") {
      if (value.value || value.title) {
        return flattenToText(value.value || value.title)
      }

      return flattenToText(Object.values(value))
    }

    if (value === null || value === undefined) {
      return ""
    }

    return String(value)
  }

  const normalizedSource = flattenToText(source).toLowerCase()
  if (!normalizedSource) {
    return false
  }

  return selectedValues.some((value) => {
    const normalizedValue = normalizeSelection(value)
    return normalizedValue && normalizedSource.includes(normalizedValue)
  })
}

const careerMatchesRefinements = (career, refinementList) => {
  const selectedActivity = refinementList[CAREERS_FILTER_KEYS.activity] || []
  const selectedDress = refinementList[CAREERS_FILTER_KEYS.dress] || []
  const selectedEducation = refinementList[CAREERS_FILTER_KEYS.education] || []
  const selectedSalary = refinementList[CAREERS_FILTER_KEYS.salary] || []
  const selectedPersonality = refinementList[CAREERS_FILTER_KEYS.personality] || []
  const selectedPathways = refinementList[CAREERS_FILTER_KEYS.pathways] || []

  const salaryMedian = Number(career.career_salary_median)
  const salaryMatches = !selectedSalary.length || selectedSalary.some((salaryLabel) => {
    const salaryRange = SALARY_RANGE_BY_IMPORTANCE[salaryLabel]

    if (!salaryRange || !Number.isFinite(salaryMedian)) {
      return false
    }

    return salaryRange[0] <= salaryMedian && salaryMedian < salaryRange[1]
  })

  const educationMatches = includesAnyValue(career.career_education_level, selectedEducation)
  const dressMatches = includesAnyValue(career.career_dress, selectedDress)
  const activityMatches = includesAnyValue(career.career_activity, selectedActivity)
  const personalitySource = career.career_personality_filter || career.career_keywords
  const personalityMatches = includesAnyValue(personalitySource, selectedPersonality)
  const pathwayMatches = includesAnyValue(career.career_pathways_filter, selectedPathways)

  return salaryMatches && educationMatches && dressMatches && activityMatches && personalityMatches && pathwayMatches
}

const toCategoryText = (categories) => {
  if (Array.isArray(categories)) {
    return categories.map((item) => item?.title || item?.value || item).filter(Boolean).join(" ")
  }

  if (categories && typeof categories === "object") {
    return categories.title || categories.value || ""
  }

  return categories || ""
}

const normalizeText = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeText(item)).join(" ")
  }

  if (value === null || value === undefined) {
    return ""
  }

  return String(value).toLowerCase().trim()
}

// career_synonyms comes through as a single comma-separated string (or array of such
// strings). Splitting it into individual trimmed values lets us tell whether the query is
// an exact match against one specific synonym vs just a substring match somewhere in the
// combined blob, so exact matches can be ranked above partial ones.
const splitSynonymsField = (value) => {
  if (!value) {
    return []
  }

  if (Array.isArray(value)) {
    return value.flatMap((item) => splitSynonymsField(item)).filter(Boolean)
  }

  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean)
  }

  if (value?.value || value?.title) {
    return splitSynonymsField(value.value || value.title)
  }

  return []
}

const getRelatedCareerTitles = (value) => {
  if (!value) {
    return []
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") {
          return item
        }

        return item?.title
      })
      .filter(Boolean)
  }

  if (typeof value === "string") {
    return [value]
  }

  if (value?.title) {
    return [value.title]
  }

  return []
}

let lastParsedQuery = ""
let lastParsedQueryTokens = []

const getQueryTokens = (query) => {
  const normalizedQuery = normalizeText(query)

  if (!normalizedQuery) {
    return { normalizedQuery: "", tokens: [] }
  }

  if (normalizedQuery === lastParsedQuery) {
    return { normalizedQuery, tokens: lastParsedQueryTokens }
  }

  const tokens = normalizedQuery.split(/\s+/).filter(Boolean)
  lastParsedQuery = normalizedQuery
  lastParsedQueryTokens = tokens

  return { normalizedQuery, tokens }
}

const careerMatchesSearchTokens = (career, query) => {
  const { normalizedQuery, tokens } = getQueryTokens(query)

  if (!normalizedQuery) {
    return true
  }

  if (normalizedQuery.length < 2) {
    return true
  }

  if (!tokens.length) {
    return true
  }

  const synonymsText = career?.career_synonyms_text || ""
  const hasSynonymMatch = Boolean(synonymsText) && (
    synonymsText.includes(normalizedQuery)
    || tokens.some((token) => synonymsText.includes(token))
  )

  if (hasSynonymMatch) {
    return true
  }

  const searchableText = career?.career_searchable_text || ""

  return tokens.every((token) => searchableText.includes(token))
}

const getCareerSynonymQueryPriority = (career, query) => {
  const { normalizedQuery, tokens } = getQueryTokens(query)

  if (!normalizedQuery || !tokens.length) {
    return 0
  }

  const synonymsList = career?.career_synonyms_list || []
  if (!synonymsList.length) {
    return 0
  }

  const normalizedSynonyms = synonymsList.map((synonym) => normalizeText(synonym)).filter(Boolean)
  if (!normalizedSynonyms.length) {
    return 0
  }

  // Highest priority: the query is an exact match against one of the individual,
  // comma-separated career synonym values (not just a substring hit anywhere in the
  // combined blob). This surfaces a precise synonym match above a partial match.
  const hasExactMatch = normalizedSynonyms.some((synonym) => synonym === normalizedQuery)
  if (hasExactMatch) {
    return 2
  }

  // Second priority: substring match, checked per individual synonym so a match can't
  // accidentally span across two different concatenated synonym values.
  const hasPartialMatch = normalizedSynonyms.some((synonym) => {
    return synonym.includes(normalizedQuery) || tokens.some((token) => synonym.includes(token))
  })

  return hasPartialMatch ? 1 : 0
}

// Personality match strength (Excellent/Good/Fair labels) is derived directly from this
// numeric correlation/match score via calcMatchStrength, so sorting by the raw score
// descending puts the strongest matches first and inherently respects the label tiers.
const getSortableMatchScore = (correlation) => {
  const numericCorrelation = Number(correlation)
  return Number.isFinite(numericCorrelation) ? numericCorrelation : -Infinity
}

// Restores the pre-refactor combined-match ordering rule:
// 1. Careers with a TAL-E recommendation (stay steady / change course) rank above
//    careers with only a personality match.
// 2. Within each group, higher personality match quality (Excellent > Good > Fair > none)
//    ranks earlier - so a career that is both a strong personality match AND a TAL-E
//    recommendation surfaces at the very top of the combined list.
// 3. Ties (e.g. no personality match at all) are broken by recommendation type, with
//    Stay Steady ranked before Change Course.
const MATCH_QUALITY_RANK = {
  Excellent: 0,
  Good: 1,
  Fair: 2,
  none: 3,
}

const MATCH_RECOMMENDATION_RANK = {
  [MATCH_TYPE_STAY_STEADY]: 0,
  [MATCH_TYPE_CHANGE_COURSE]: 1,
}

const getMatchQualityRank = (correlation) => {
  const numericCorrelation = Number(correlation)
  const quality = Number.isFinite(numericCorrelation) ? calcMatchStrength(numericCorrelation) : "none"

  return MATCH_QUALITY_RANK[quality] ?? MATCH_QUALITY_RANK.none
}

const sortCombinedCareerMatches = (careers) => {
  return [...careers].sort((a, b) => {
    const aHasRecommendation = Boolean(a?.recommendationLabel)
    const bHasRecommendation = Boolean(b?.recommendationLabel)

    if (aHasRecommendation !== bHasRecommendation) {
      return aHasRecommendation ? -1 : 1
    }

    const qualityDiff = getMatchQualityRank(a?.correlation) - getMatchQualityRank(b?.correlation)
    if (qualityDiff !== 0) {
      return qualityDiff
    }

    const aRecommendationRank = MATCH_RECOMMENDATION_RANK[a?.recommendationLabel] ?? 2
    const bRecommendationRank = MATCH_RECOMMENDATION_RANK[b?.recommendationLabel] ?? 2

    return aRecommendationRank - bRecommendationRank
  })
}

const mapCareerToCard = (career, context = {}) => {
  const { sortByPage } = context

  if (sortByPage === "MATCHES") {
    return (
      <WingsCareerCard
        objectID={career.id}
        img={career.career_banner_image}
        title={career.title}
        slug={career.slug}
        demand={career.career_demand}
        category={career.career_job_categories_text || toCategoryText(career.career_job_categories)}
        contentType={career.type}
        salaryEntry={career.career_salary_entry_level}
        salaryExperienced={career.career_salary_experienced}
        correlation={career.correlation}
        recommendationLabel={career.recommendationLabel}
        // TODO: when a career is disliked, immediately remove it from career match results
        onDislike={(careerSlug) => null}
      />
    )
  }

  const categoryText = career.career_job_categories_text || toCategoryText(career.career_job_categories)

  return (
    <WingsCareerCard
      objectID={career.id}
      img={career.career_banner_image}
      title={career.title}
      slug={career.slug}
      demand={career.career_demand}
      category={categoryText}
      contentType={career.type}
      salaryEntry={career.career_salary_entry_level}
      salaryExperienced={career.career_salary_experienced}
    />
  )
}

const CareersExplore = ({ classContext = "careers", filterList }) => {
  const { locale, formatMessage } = useIntl()
  const { profile, firebase } = useContext(FirebaseContext)
  const [queryFilters, setQueryFilters] = useQueryParam("queryFilters", JsonParam)
  const [matchTypeQuery, setMatchTypeQuery] = useQueryParam("matchType", StringParam)
  const [changeCourseCareers, setChangeCourseCareers] = useState([])
  const [staySteadyCareers, setStaySteadyCareers] = useState([])
  const careers = useAllCareers(locale)

  const selectedRefinements = useMemo(() => {
    return normalizeRefinementList(queryFilters?.refinementList)
  }, [queryFilters])

  const allEducationOptions = useMemo(() => ([
    { value: "High School", label: formatMessage({ id: "results-filter-high-school", defaultMessage: "High School" }) },
    { value: "License", label: formatMessage({ id: "results-filter-license", defaultMessage: "License" }) },
    { value: "Certificate", label: formatMessage({ id: "results-filter-certificate", defaultMessage: "Certificate" }) },
    { value: "Apprenticeship", label: formatMessage({ id: "results-filter-apprenticeship", defaultMessage: "Apprenticeship" }) },
    { value: "2-year College", label: formatMessage({ id: "results-filter-2-year-college", defaultMessage: "2-year College" }) },
    { value: "4-year College", label: formatMessage({ id: "results-filter-4-year-college", defaultMessage: "4-year College" }) },
    { value: "Graduate School", label: formatMessage({ id: "results-filter-graduate-school", defaultMessage: "Graduate School" }) },
  ]), [formatMessage])

  const allSalaryOptions = useMemo(() => ([
    {
      value: "Not that Important",
      label: formatMessage({ id: "results-filter-not-that-important", defaultMessage: "Not that Important" }),
    },
    {
      value: "Important",
      label: formatMessage({ id: "results-filter-important", defaultMessage: "Important" }),
    },
    {
      value: "Very Important",
      label: formatMessage({ id: "results-filter-very-important", defaultMessage: "Very Important" }),
    },
  ]), [formatMessage])

  const allPersonalityOptions = useMemo(() => ([
    { value: "Adventurous", label: "Adventurous" },
    { value: "Analytical", label: "Analytical" },
    { value: "Assertive", label: "Assertive" },
    { value: "Athletic", label: "Athletic" },
    { value: "Bold", label: "Bold" },
    { value: "Caring", label: "Caring" },
    { value: "Conventional", label: "Conventional" },
    { value: "Creative", label: "Creative" },
    { value: "Entrepreneurial", label: "Entrepreneurial" },
    { value: "Extroverted", label: "Extroverted" },
    { value: "Friendly", label: "Friendly" },
    { value: "Idealistic", label: "Idealistic" },
    { value: "Innovative", label: "Innovative" },
    { value: "Intellectual", label: "Intellectual" },
    { value: "Introverted", label: "Introverted" },
    { value: "Intuitive", label: "Intuitive" },
    { value: "Leader", label: "Leader" },
    { value: "Likes to build", label: "Likes to build" },
    { value: "Likes to debate", label: "Likes to debate" },
    { value: "Likes to help people", label: "Likes to help people" },
    { value: "Likes to perform", label: "Likes to perform" },
    { value: "Likes to teach", label: "Likes to teach" },
    { value: "Likes to work alone", label: "Likes to work alone" },
    { value: "Likes to work with hands", label: "Likes to work with hands" },
    { value: "Likes to work with numbers", label: "Likes to work with numbers" },
    { value: "Likes to work with people", label: "Likes to work with people" },
    { value: "Methodical", label: "Methodical" },
    { value: "Observant", label: "Observant" },
    { value: "Outdoorsy", label: "Outdoorsy" },
    { value: "Persuasive", label: "Persuasive" },
    { value: "Practical", label: "Practical" },
    { value: "Precise", label: "Precise" },
    { value: "Problem solver", label: "Problem solver" },
    { value: "Realistic", label: "Realistic" },
    { value: "Responsible", label: "Responsible" },
    { value: "Scientific", label: "Scientific" },
    { value: "Self-confident", label: "Self-confident" },
    { value: "Sensitive", label: "Sensitive" },
    { value: "Social", label: "Social" },
    { value: "Straightforward", label: "Straightforward" },
    { value: "Strategic", label: "Strategic" },
    { value: "Understanding", label: "Understanding" },
  ]), [])

  const allPathwaysOptions = useMemo(() => ([
    { value: "Agriculture", label: "Agriculture" },
    { value: "Construction", label: "Construction" },
    { value: "Arts, Entertainment, & Design", label: "Arts, Entertainment, & Design" },
    { value: "Management & Entrepreneurship", label: "Management & Entrepreneurship" },
    { value: "Education", label: "Education" },
    { value: "Financial Services", label: "Financial Services" },
    { value: "Public Service & Safety", label: "Public Service & Safety" },
    { value: "Healthcare & Human Services", label: "Healthcare & Human Services" },
    { value: "Hospitality, Events, & Tourism", label: "Hospitality, Events, & Tourism" },
    { value: "Digital Technology", label: "Digital Technology" },
    { value: "Advanced Manufacturing", label: "Advanced Manufacturing" },
    { value: "Marketing & Sales", label: "Marketing & Sales" },
    { value: "Supply Chain & Transportation", label: "Supply Chain & Transportation" },
    { value: "Energy & Natural Resources", label: "Energy & Natural Resources" },
  ]), [])

  const allDressOptions = useMemo(() => ([
    {
      value: "casual",
      label: formatMessage({ id: "results-filter-casual", defaultMessage: "Casual" }),
    },
    {
      value: "business casual",
      label: formatMessage({ id: "results-filter-business-casual", defaultMessage: "Business Casual" }),
    },
    {
      value: "business",
      label: formatMessage({ id: "results-filter-business", defaultMessage: "Business" }),
    },
    {
      value: "uniform",
      label: formatMessage({ id: "results-filter-uniform", defaultMessage: "Uniform" }),
    },
  ]), [formatMessage])

  const allActivityOptions = useMemo(() => ([
    {
      value: "stationary",
      label: formatMessage({ id: "results-filter-stationary", defaultMessage: "Stationary" }),
    },
    {
      value: "moderately active",
      label: formatMessage({ id: "results-filter-moderately-active", defaultMessage: "Moderately Active" }),
    },
    {
      value: "active",
      label: formatMessage({ id: "results-filter-active", defaultMessage: "Active" }),
    },
  ]), [formatMessage])

  const toggleRefinement = (filterKey, value, isChecked) => {
    const currentList = normalizeRefinementList(queryFilters?.refinementList)
    const currentValues = currentList[filterKey]
    const nextValues = isChecked
      ? [...new Set([...currentValues, value])]
      : currentValues.filter((item) => item !== value)

    const nextRefinementList = {
      ...currentList,
      [filterKey]: nextValues,
    }

    setQueryFilters({
      ...(queryFilters || {}),
      page: 1,
      refinementList: nextRefinementList,
    })
  }

  const clearRefinements = () => {
    setQueryFilters({
      ...(queryFilters || {}),
      page: 1,
      refinementList: normalizeRefinementList(),
    })
  }

  const selectedFilterOptionIds = useMemo(() => {
    return Object.entries(selectedRefinements).flatMap(([filterKey, values]) => {
      return (values || []).map((value) => `${filterKey}${FILTER_ID_SEPARATOR}${value}`)
    })
  }, [selectedRefinements])

  const personalityMatches = normalizeCareerMatches(profile?.assessmentRecord?.careerMatches)
  const taleChangeCourseCareers = normalizeCareerMatches(changeCourseCareers)
  const taleStaySteadyCareers = normalizeCareerMatches(staySteadyCareers)

  const hasPersonalityMatches = personalityMatches.length > 0
  const hasStaySteadyMatches = taleStaySteadyCareers.length > 0
  const hasChangeCourseMatches = taleChangeCourseCareers.length > 0
  const hasCombinedMatches = hasPersonalityMatches || hasStaySteadyMatches || hasChangeCourseMatches

  const availableMatchTypes = useMemo(() => {
    const types = []

    if (hasCombinedMatches) {
      types.push(MATCH_TYPE_COMBINED)
    }
    if (hasPersonalityMatches) {
      types.push(MATCH_TYPE_PERSONALITY)
    }
    if (hasStaySteadyMatches) {
      types.push(MATCH_TYPE_STAY_STEADY)
    }
    if (hasChangeCourseMatches) {
      types.push(MATCH_TYPE_CHANGE_COURSE)
    }

    return types
  }, [hasCombinedMatches, hasPersonalityMatches, hasStaySteadyMatches, hasChangeCourseMatches])

  const activeMatchType = useMemo(() => {
    if (availableMatchTypes.includes(matchTypeQuery)) {
      return matchTypeQuery
    }

    return availableMatchTypes[0] || MATCH_TYPE_COMBINED
  }, [availableMatchTypes, matchTypeQuery])

  useEffect(() => {
    const fetchSavedResponses = async () => {
      if (firebase?.getUserTaleResponsesAndMatches) {
        try {
          const savedResponses = await firebase.getUserTaleResponsesAndMatches()
          const { data } = savedResponses || {}
          const { careerMatches } = data || {}
          const { changeCourseCareers = [], staySteadyCareers = [] } = careerMatches || {}

          setChangeCourseCareers(changeCourseCareers)
          setStaySteadyCareers(staySteadyCareers)
        } catch (error) {
          console.error("Error fetching TAL-E matches:", error)
        }
      }
    }

    fetchSavedResponses()
  }, [firebase])

  useEffect(() => {
    if (!availableMatchTypes.length) {
      return
    }

    if (!availableMatchTypes.includes(matchTypeQuery)) {
      setMatchTypeQuery(availableMatchTypes[0])
    }
  }, [availableMatchTypes, matchTypeQuery, setMatchTypeQuery])

  const careersWithSearchText = useMemo(() => {
    return careers.map((career) => {
      const careerKeywordsText = Array.isArray(career.career_keywords)
        ? career.career_keywords.filter(Boolean).join(" ")
        : (career.career_keywords || "")

      const careerJobCategoriesText = toCategoryText(career.career_job_categories)
      const careerPathwaysText = Array.isArray(career.career_pathways_filter)
        ? career.career_pathways_filter.join(" ")
        : (career.career_pathways_filter || "")
      const careerSynonymsList = splitSynonymsField(career.career_synonyms)
      const careerSynonymsText = normalizeText(careerSynonymsList)
      const careerRelatedCareersTitles = getRelatedCareerTitles(career.career_related_careers)
      const careerRelatedCareersTitlesText = normalizeText(careerRelatedCareersTitles)

      return {
        ...career,
        career_keywords_text: careerKeywordsText,
        career_synonyms_list: careerSynonymsList,
        career_synonyms_text: careerSynonymsText,
        career_related_careers_titles_text: careerRelatedCareersTitlesText,
        career_job_categories_text: careerJobCategoriesText,
        career_pathways_text: careerPathwaysText,
        career_searchable_text: normalizeText([
          career.title,
          careerRelatedCareersTitlesText,
          careerSynonymsText,
          careerKeywordsText,
          careerJobCategoriesText,
          careerPathwaysText,
          career.career_education_level,
        ]),
      }
    })
  }, [careers])

  const hasMatchingCareerForRefinements = (nextRefinements) => {
    return careersWithSearchText.some((career) => careerMatchesRefinements(career, nextRefinements))
  }

  const getVisibleOptions = (options, filterKey) => {
    if (!ENABLE_EXCLUSIVE_OPTION_HIDING) {
      return options
    }

    const selectedValues = selectedRefinements[filterKey] || []

    return options.filter((option) => {
      if (selectedValues.includes(option.value)) {
        return true
      }

      const nextRefinements = {
        ...selectedRefinements,
        [filterKey]: [...selectedValues, option.value],
      }

      return hasMatchingCareerForRefinements(nextRefinements)
    })
  }

  const educationOptions = useMemo(() => {
    return getVisibleOptions(allEducationOptions, CAREERS_FILTER_KEYS.education)
  }, [allEducationOptions, selectedRefinements, careersWithSearchText])

  const personalityOptions = useMemo(() => {
    return getVisibleOptions(allPersonalityOptions, CAREERS_FILTER_KEYS.personality)
  }, [allPersonalityOptions, selectedRefinements, careersWithSearchText])

  const salaryOptions = useMemo(() => {
    return getVisibleOptions(allSalaryOptions, CAREERS_FILTER_KEYS.salary)
  }, [allSalaryOptions, selectedRefinements, careersWithSearchText])

  const pathwaysOptions = useMemo(() => {
    return allPathwaysOptions
  }, [allPathwaysOptions])

  const dressOptions = useMemo(() => {
    return getVisibleOptions(allDressOptions, CAREERS_FILTER_KEYS.dress)
  }, [allDressOptions, selectedRefinements, careersWithSearchText])

  const activityOptions = useMemo(() => {
    return getVisibleOptions(allActivityOptions, CAREERS_FILTER_KEYS.activity)
  }, [allActivityOptions, selectedRefinements, careersWithSearchText])

  const filterSections = useMemo(() => {
    return [
      {
        id: CAREERS_FILTER_KEYS.pathways,
        title: formatMessage({ id: "results-filter-pathways", defaultMessage: "Pathways" }),
        columns: 3,
        options: pathwaysOptions.map((option) => ({
          id: `${CAREERS_FILTER_KEYS.pathways}${FILTER_ID_SEPARATOR}${option.value}`,
          label: option.label,
        })),
      },
      {
        id: CAREERS_FILTER_KEYS.personality,
        title: formatMessage({ id: "results-filter-personality", defaultMessage: "Personality" }),
        columns: 3,
        options: personalityOptions.map((option) => ({
          id: `${CAREERS_FILTER_KEYS.personality}${FILTER_ID_SEPARATOR}${option.value}`,
          label: option.label,
        })),
      },
      {
        id: CAREERS_FILTER_KEYS.education,
        title: formatMessage({ id: "results-filter-education-required", defaultMessage: "Education Required" }),
        columns: 3,
        options: educationOptions.map((option) => ({
          id: `${CAREERS_FILTER_KEYS.education}${FILTER_ID_SEPARATOR}${option.value}`,
          label: option.label,
        })),
      },
      {
        id: CAREERS_FILTER_KEYS.salary,
        title: formatMessage({ id: "results-filter-salary", defaultMessage: "Salary" }),
        columns: 3,
        options: salaryOptions.map((option) => ({
          id: `${CAREERS_FILTER_KEYS.salary}${FILTER_ID_SEPARATOR}${option.value}`,
          label: option.label,
        })),
      },
      {
        id: CAREERS_FILTER_KEYS.dress,
        title: formatMessage({ id: "results-filter-dress", defaultMessage: "Dress" }),
        columns: 3,
        options: dressOptions.map((option) => ({
          id: `${CAREERS_FILTER_KEYS.dress}${FILTER_ID_SEPARATOR}${option.value}`,
          label: option.label,
        })),
      },
      {
        id: CAREERS_FILTER_KEYS.activity,
        title: formatMessage({ id: "results-filter-physical-activity", defaultMessage: "Physical Activity" }),
        columns: 3,
        options: activityOptions.map((option) => ({
          id: `${CAREERS_FILTER_KEYS.activity}${FILTER_ID_SEPARATOR}${option.value}`,
          label: option.label,
        })),
      },
    ]
  }, [
    formatMessage,
    pathwaysOptions,
    personalityOptions,
    educationOptions,
    salaryOptions,
    dressOptions,
    activityOptions,
  ])

  const handleUnifiedFilterToggle = (optionId) => {
    const [filterKey, value] = optionId.split(FILTER_ID_SEPARATOR)

    if (!filterKey || !value) {
      return
    }

    const isChecked = selectedRefinements[filterKey]?.includes(value)
    toggleRefinement(filterKey, value, !isChecked)
  }

  const filteredCareersWithSearchText = useMemo(() => {
    return careersWithSearchText.filter((career) => careerMatchesRefinements(career, selectedRefinements))
  }, [careersWithSearchText, selectedRefinements])

  const careersWithMatchMetadata = useMemo(() => {
    const careersBySlug = new Map(filteredCareersWithSearchText.map((career) => [career.slug, career]))
    const staySteadySlugs = new Set(taleStaySteadyCareers.map((career) => career.slug))
    const changeCourseSlugs = new Set(taleChangeCourseCareers.map((career) => career.slug))

    const personalityCareers = removeMissingCareers(personalityMatches, filteredCareersWithSearchText)
      .map((personalityMatch) => {
        const matchedCareer = careersBySlug.get(personalityMatch.slug)

        if (!matchedCareer) {
          return null
        }

        let recommendationLabel
        if (staySteadySlugs.has(matchedCareer.slug)) {
          recommendationLabel = MATCH_TYPE_STAY_STEADY
        } else if (changeCourseSlugs.has(matchedCareer.slug)) {
          recommendationLabel = MATCH_TYPE_CHANGE_COURSE
        }

        return {
          ...matchedCareer,
          correlation: personalityMatch.correlation,
          recommendationLabel,
        }
      })
      .filter(Boolean)

    if (activeMatchType === MATCH_TYPE_PERSONALITY) {
      return personalityCareers
    }

    if (activeMatchType === MATCH_TYPE_STAY_STEADY) {
      return taleStaySteadyCareers
        .map((match) => careersBySlug.get(match.slug))
        .filter(Boolean)
        .map((career) => ({
          ...career,
          recommendationLabel: MATCH_TYPE_STAY_STEADY,
        }))
    }

    if (activeMatchType === MATCH_TYPE_CHANGE_COURSE) {
      return taleChangeCourseCareers
        .map((match) => careersBySlug.get(match.slug))
        .filter(Boolean)
        .map((career) => ({
          ...career,
          recommendationLabel: MATCH_TYPE_CHANGE_COURSE,
        }))
    }

    const combinedBySlug = new Map()

    personalityCareers.forEach((career) => {
      combinedBySlug.set(career.slug, career)
    })

    taleStaySteadyCareers.forEach((match) => {
      if (!combinedBySlug.has(match.slug)) {
        const matchedCareer = careersBySlug.get(match.slug)
        if (matchedCareer) {
          combinedBySlug.set(match.slug, {
            ...matchedCareer,
            recommendationLabel: MATCH_TYPE_STAY_STEADY,
          })
        }
      }
    })

    taleChangeCourseCareers.forEach((match) => {
      if (!combinedBySlug.has(match.slug)) {
        const matchedCareer = careersBySlug.get(match.slug)
        if (matchedCareer) {
          combinedBySlug.set(match.slug, {
            ...matchedCareer,
            recommendationLabel: MATCH_TYPE_CHANGE_COURSE,
          })
        }
      }
    })

    return sortCombinedCareerMatches(Array.from(combinedBySlug.values()))
  }, [
    filteredCareersWithSearchText,
    personalityMatches,
    taleStaySteadyCareers,
    taleChangeCourseCareers,
    activeMatchType,
  ])

  const sortOptions = useMemo(() => {
    const baseOptions = [
      { key: "NEWEST", labelId: "all", defaultMessage: "All" },
    ]

    if (hasCombinedMatches) {
      baseOptions.push({ key: "MATCHES", labelId: "matches", defaultMessage: "Matches" })
    }

    baseOptions.push({ key: "SAVED", labelId: "algolica-careers-explore-saved", defaultMessage: "Saved" })

    return baseOptions
  }, [hasCombinedMatches])

  const careersWithMatchMetadataBySlug = useMemo(() => {
    return new Map(careersWithMatchMetadata.map((career) => [career.slug, career]))
  }, [careersWithMatchMetadata])

  const careersSearchOptions = useMemo(() => ({
    threshold: 0.35,
    ignoreLocation: true,
  }), [])

  const getItemsForSortMode = useCallback((items, sortMode) => {
    if (sortMode === "MATCHES") {
      const matchSlugs = new Set(careersWithMatchMetadataBySlug.keys())
      const matchedItems = items
        .filter((career) => matchSlugs.has(career.slug))
        .map((career) => careersWithMatchMetadataBySlug.get(career.slug) || career)

      if (activeMatchType === MATCH_TYPE_PERSONALITY) {
        return [...matchedItems].sort((a, b) => {
          return getSortableMatchScore(b?.correlation) - getSortableMatchScore(a?.correlation)
        })
      }

      if (activeMatchType === MATCH_TYPE_COMBINED) {
        return sortCombinedCareerMatches(matchedItems)
      }

      return matchedItems
    }

    return items
  }, [careersWithMatchMetadataBySlug, activeMatchType])

  const handleMatchTypeChange = (nextMatchType) => {
    if (activeMatchType === nextMatchType) {
      return
    }

    setMatchTypeQuery(nextMatchType)
    setQueryFilters({ ...(queryFilters || {}), page: 1 })
  }

  return (
    <LocalExplore
      classContext={classContext}
      filterList={filterList}
      items={filteredCareersWithSearchText}
      searchKeys={careersSearchKeys}
      hitsPerPage={CAREERS_PER_PAGE}
      getRecencyDate={(item) => item.created_at || item.modified_at || item.published_at}
      sortOptions={sortOptions}
      getItemsForSortMode={getItemsForSortMode}
      renderSortExtras={({ sortByPage }) => {
        const showMatchTypes = sortByPage === "MATCHES" && hasCombinedMatches

        return (
          <>
            {showMatchTypes && (
              <div className="results-match-type-toggle">
                <div className="results-match-type-toggle-options">
                  {hasCombinedMatches && (
                    <div className="results-match-type-toggle-option">
                      <RadioButton
                        id="career-match-type-combined"
                        inputId="career-match-type-combined"
                        name="career-match-type"
                        value={MATCH_TYPE_COMBINED}
                        checked={activeMatchType === MATCH_TYPE_COMBINED}
                        onChange={(event) => handleMatchTypeChange(event.value)}
                      />
                      <label className="results-match-type-toggle-option-label" htmlFor="career-match-type-combined">
                        {formatMessage({ id: "careers-match-type-combined", defaultMessage: "Combined" })}
                      </label>
                    </div>
                  )}

                  {hasPersonalityMatches && (
                    <div className="results-match-type-toggle-option">
                      <RadioButton
                        id="career-match-type-personality"
                        inputId="career-match-type-personality"
                        name="career-match-type"
                        value={MATCH_TYPE_PERSONALITY}
                        checked={activeMatchType === MATCH_TYPE_PERSONALITY}
                        onChange={(event) => handleMatchTypeChange(event.value)}
                      />
                      <label className="results-match-type-toggle-option-label" htmlFor="career-match-type-personality">
                        {formatMessage({ id: "careers-match-type-personality", defaultMessage: "Interest Profiler" })}
                      </label>
                    </div>
                  )}

                  {hasStaySteadyMatches && (
                    <div className="results-match-type-toggle-option">
                      <RadioButton
                        id="career-match-type-stay-steady"
                        inputId="career-match-type-stay-steady"
                        name="career-match-type"
                        value={MATCH_TYPE_STAY_STEADY}
                        checked={activeMatchType === MATCH_TYPE_STAY_STEADY}
                        onChange={(event) => handleMatchTypeChange(event.value)}
                      />
                      <label className="results-match-type-toggle-option-label" htmlFor="career-match-type-stay-steady">
                        {formatMessage({ id: "careers-match-type-stay-steady", defaultMessage: "Stay Steady" })}
                      </label>
                    </div>
                  )}

                  {hasChangeCourseMatches && (
                    <div className="results-match-type-toggle-option">
                      <RadioButton
                        id="career-match-type-change-course"
                        inputId="career-match-type-change-course"
                        name="career-match-type"
                        value={MATCH_TYPE_CHANGE_COURSE}
                        checked={activeMatchType === MATCH_TYPE_CHANGE_COURSE}
                        onChange={(event) => handleMatchTypeChange(event.value)}
                      />
                      <label className="results-match-type-toggle-option-label" htmlFor="career-match-type-change-course">
                        {formatMessage({ id: "careers-match-type-change-course", defaultMessage: "Change Course" })}
                      </label>
                    </div>
                  )}
                </div>
              </div>
            )}

            <ExploreUnifiedFiltersDropdown
              heading={formatMessage({ id: "filter-careers-by", defaultMessage: "Filter Careers by" })}
              viewLabel={formatMessage({ id: "algolia-toggle-dropdown-careers", defaultMessage: "Careers" })}
              sections={filterSections}
              selectedOptionIds={selectedFilterOptionIds}
              onToggleOption={handleUnifiedFilterToggle}
              onClearAll={clearRefinements}
            />
          </>
        )
      }}
      searchPlaceholder={formatMessage({
        id: "algolia-careers-explore-search-placeholder",
        defaultMessage: "Search by career name, category",
      })}
      preserveSearchRelevanceOrder={true}
      searchOptions={careersSearchOptions}
      filterItemByQuery={careerMatchesSearchTokens}
      getQueryPriority={getCareerSynonymQueryPriority}
      fallbackToUnfilteredOnPostFilterEmpty={true}
      renderCard={mapCareerToCard}
    />
  )
}

export default CareersExplore
